import { Field, Form, Formik, FormikErrors } from "formik"
import useTranslation from "next-translate/useTranslation"
import Link from "next/link"
import { useEffect } from "react"
import { useDispatch } from "react-redux"
import { Button, Collapse, FormGroup } from "reactstrap"

import { ICategory, IPublicStatistics, ISDG } from "@api/schema"
import DropdownComponent from "@components/common/DropdownComponent"
import FilterItem from "@components/common/FilterItem"
import FormikInput from "@components/common/form/FormikInput"
import Icon from "@components/common/Icon"
import SDGElement from "@components/common/SDGElement"
import { withDynamicNamespaces } from "@components/hoc/withDynamicNamespaces"
import MarketStatsDisplay from "@components/marketplace/MarketStatsDisplay"
import { IFormikActions } from "@redux/helper/actions"
import {
  IMarketSearchParams,
  IMarketSortOrder,
  IMarketSortType,
  MarketProjectFilterType,
  setMarketSearchParamsAction,
} from "@redux/usecases/marketplace"
import { useToggleState } from "@services/hooks/useToggleState"
import { NamespaceShortcut, useDynamicTranslation } from "@services/i18n"
import { Routes } from "@services/routes"
import { useTooltip } from "@services/tooltipHook"
import { idExistsInEntities } from "@services/util"
import { MODULE_SDG_AVAILABLE } from "config"

const usedNamespaces: NamespaceShortcut[] = ["sdg"]

export const ID_MARKET_SEARCH_FORM = "market_search_form"

const validate = (values: IMarketSearchParams): FormikErrors<IMarketSearchParams> => {
  const errors: { [key: string]: string } = {}

  if (values.pattern && values.pattern.length < 3) {
    errors.pattern = "validate.marketSearch.patternTooShort"
  }

  return errors
}

interface IProps {
  categories: ICategory[]
  sdgs: ISDG[]
  initialCategoryId?: number
  initialSDGId?: number
  searchParams: IMarketSearchParams
  publicStatistics: IPublicStatistics
}

/**
 * this component shows a form to search for projects and filter them for categories
 * to be used at the (public) marketplace to filter the projects
 * it also shows statistic information about users, projects and project ideas
 * and allows the user to switch between different MarketProjectFilterTypes:
 * Ideas, Projects in planning state, running projects, finished projects
 *
 * explanation: MarketProjectFilterType will be "translated" to filter criteria in the
 * loadMarketProjectsSaga into IProject.progress and IProject.implementationState
 * Because those filter-criterias are combined by AND to search the database, it is not possible
 * to filter for ideas (based on IProject.progress) and finished projects (based on IProject.implementationState)
 * in OR-combination. Thats why the user is allowed to filter for ideas on the one hand
 * and the other (implementation-)states on the other hand
 *
 * for filtering projects by SDGs or categories the ideas must be left away, because they do not have category/sdg
 *
 * @todo change summary count of project and ideas to real results from given filters
 * @todo: ideas are not shown if categories are chosen and the user changes to idea-tab
 */
const MarketSearchForm: React.FC<IProps> = ({
  searchParams,
  categories,
  sdgs,
  initialCategoryId,
  initialSDGId,
  publicStatistics
}: IProps) => {
  const { t } = useTranslation("common")
  const dispatch = useDispatch()
  const td = useDynamicTranslation()

  const categoryToggleTooltip = useTooltip(t("form.marketplace.categoryFilter.tooltip"))
  const sdgToggleTooltip = useTooltip(t("form.marketplace.sdgFilter.tooltip"))

  const { isTrue: categoriesExpanded, toggle: toggleCategoriesExpanded, setIsTrue: setCategoriesExpanded } = useToggleState(false)
  const { isTrue: sdgsExpanded, toggle: toggleSdgsExpanded, setIsTrue: setSdgsExpanded } = useToggleState(false)

  useEffect(() => {
    // category filter open by default when an initial category is given
    setCategoriesExpanded(idExistsInEntities(initialCategoryId, categories))
    // sdg filter open by default when an initial sdg is given
    setSdgsExpanded(MODULE_SDG_AVAILABLE && idExistsInEntities(initialSDGId, sdgs))
  }, [])

  // close unchosen form-elements (category, SDG) when another form-element
  // is chosen
  useEffect(() => {
    if (sdgsExpanded) {
      setCategoriesExpanded(false)
    }
  }, [sdgsExpanded])

  useEffect(() => {
    if (categoriesExpanded) {
      setSdgsExpanded(false)
    }
  }, [categoriesExpanded])

  // open category filter when initialCategoryId is given
  // and there is a category with this initialCategoryId
  useEffect(() => {
    if (idExistsInEntities(initialCategoryId, categories)) {
      setCategoriesExpanded(true)
    }
  }, [initialCategoryId, categories])

  // open sdg filter when initialSDGId is given
  // and there is a SDG with this initialSDGId
  useEffect(() => {
    if (idExistsInEntities(initialSDGId, sdgs)) {
      setSdgsExpanded(true)
    }
  }, [initialSDGId, sdgs])

  // projectFilter-Buttons are active on special situations:
  const ideaButtonChosen = searchParams.projectTypeFilter?.includes(MarketProjectFilterType.Ideas)
  const planningProjectsButtonChosen = searchParams.projectTypeFilter?.includes(MarketProjectFilterType.ProjectsInPlanning)
  const runningProjectsButtonChosen = searchParams.projectTypeFilter?.includes(MarketProjectFilterType.ProjectsRunning)
  const finishedProjectsButtonChosen = searchParams.projectTypeFilter?.includes(MarketProjectFilterType.ProjectsFinished)

  // the sorting options, depending on the type of projects
  const sortingOptions: IMarketSortType[] =
    ideaButtonChosen
      ? [IMarketSortType.CreatedAt]
      : planningProjectsButtonChosen || runningProjectsButtonChosen || finishedProjectsButtonChosen
        ? [
          IMarketSortType.CreatedAt,
          IMarketSortType.UpdatedAt,
          IMarketSortType.Name,
          IMarketSortType.MemberCount,
        ]
        : []

  /**
   * triggered on changes by the MarketSearchForm
   * actions may be optional, b/c onSubmit is sometimes called directly within the MarketSearchForm
   * and not by a formik form
   */
  const onSubmit = (values: IMarketSearchParams, actions?: IFormikActions): void => {
    const tempSearchParams: IMarketSearchParams = { ...values }

    if (values.pattern) {
      // Workaround for https://futureprojects.atlassian.net/browse/FCP-1376
      // b/c “uppercase umlaute” like “Ü” are currently not supported by the api
      // adapted not the form itself,
      // because the user input should not be changed to avoid confusion
      tempSearchParams.pattern = values.pattern.toLowerCase()
    }
    dispatch(setMarketSearchParamsAction(values))

    // optional, b/c actions may not be set when method is called directly
    actions?.setSubmitting(false)
  }


  return (
    <Formik<IMarketSearchParams>
      initialValues={searchParams}
      onSubmit={onSubmit}
      validate={validate}
      validateOnChange={false}
      // We need this prop to make formik re-render the form when initialValues change
      // see https://github.com/jaredpalmer/formik/issues/811 and https://formik.org/docs/api/formik#enablereinitialize-boolean
      enableReinitialize
    >
      {({ handleSubmit, setFieldValue, values }) => {
        return <Form
          onSubmit={handleSubmit}
          id={ID_MARKET_SEARCH_FORM}
        >
          <>
            <FormGroup className="marketplace-search-container">
              <span className="icon" onClick={() => onSubmit(values)}>
                <Icon
                  name="search"
                  size={18}
                  title={t("form.marketplace.search")}
                />
              </span>
              <Field
                component={FormikInput}
                help="form.marketplace.pattern.help"
                label="form.marketplace.pattern.label"
                placeholder={`form.marketplace.pattern.placeholder.projects`}
                maxLength={50}
                name="pattern"
                noGroup={true}
                type="text"
                value={values.pattern}
                className="marketplace-search"
              />
            </FormGroup>
            {// only show category-filter, if projects are shown (and not ideas or extended categories)
              (planningProjectsButtonChosen || runningProjectsButtonChosen || finishedProjectsButtonChosen) && (
                <div className="project-filtering">
                  {// Category-Filter elements
                    categories?.length > 0 &&
                    <Collapse isOpen={categoriesExpanded} className="project-filtering__collapse-show">
                      <div>
                        {categories.map((category) => (
                          <FilterItem
                            key={category.id}
                            isActive={searchParams.categories?.includes(category.id)}
                            onClick={() => {
                              const activeCategories: number[] =
                                searchParams.categories?.slice() || []
                              if (activeCategories.includes(category.id)) {
                                activeCategories.splice(
                                  activeCategories.indexOf(category.id),
                                  1
                                )
                              } else {
                                activeCategories.push(category.id)
                              }
                              setFieldValue("categories", activeCategories) // set the chosen categories
                              setFieldValue("sdgs", []) // reset the sdgs

                              onSubmit({
                                ...values,
                                categories: activeCategories,
                                sdgs: [] // reset SDGs
                              })
                            }}
                          >
                            {category.title}
                          </FilterItem>
                        ))}
                      </div>
                    </Collapse>
                  }

                  {// SDG filter elements
                    MODULE_SDG_AVAILABLE && sdgs?.length > 0 &&
                    <Collapse isOpen={sdgsExpanded} className="project-filtering__collapse-show">
                      <div className="icon-filteritem-container">
                        {sdgs.map((sdg) => (
                          <FilterItem
                            key={sdg.id}
                            isActive={searchParams.sdgs?.includes(sdg.id)}
                            onClick={() => {
                              const activeSDGs: number[] =
                                searchParams.sdgs?.slice() || []
                              if (activeSDGs.includes(sdg.id)) {
                                activeSDGs.splice(
                                  activeSDGs.indexOf(sdg.id),
                                  1
                                )
                              } else {
                                activeSDGs.push(sdg.id)
                              }

                              setFieldValue("sdgs", activeSDGs) // set the chosen SDGs
                              setFieldValue("categories", []) // reset the categories
                              onSubmit({
                                ...values,
                                sdgs: activeSDGs,
                                categories: [] // reset categories
                              })
                            }}
                            tooltipText={td("sdg", `title.${sdg.id}`)}
                            isImage
                          >
                            <SDGElement
                              sdg={sdg}
                              width={90}
                              tooltip={false} // b/c of surrounding FilterItem tooltip
                            />
                          </FilterItem>
                        ))}
                      </div>
                    </Collapse>
                  }
                  <div className="btn-flex-layout mt-3">
                    {categories?.length > 0 && <>
                      { // category-button, depending on: categories chosen or not?
                        searchParams.categories?.length > 0 // are categories chosen?
                          ? <Button // Button for removing filtering categories when at least one is chosen
                            className="project-filtering__btn"
                            id="category-filtering-toggler"
                            onClick={() => {
                              setCategoriesExpanded(false)
                              setFieldValue("categories", [])
                              onSubmit({ ...values, categories: [] })
                            }}
                            type="button"
                          >
                            {t("form.marketplace.categoryFilter.removeFilter")}
                            <Icon
                              name="trash"
                              size={25}
                              title={t("form.marketplace.categoryFilter.removeFilter")}
                            />
                          </Button>
                          : <>
                            <Button // Button for showing/hiding the categories
                              className="project-filtering__btn"
                              id="category-filtering-toggler"
                              onClick={toggleCategoriesExpanded}
                              type="button"
                              disabled={!planningProjectsButtonChosen
                                && !runningProjectsButtonChosen
                                && !finishedProjectsButtonChosen
                              } // disable the button, if only ideas would be shown
                              innerRef={categoryToggleTooltip.tooltipTargetRef}
                            >
                              {t("form.marketplace.categoryFilter.label")}
                              <Icon
                                className={`${categoriesExpanded ? " project-filtering--active" : ""}`}
                                name="chevron-down"
                                size={25}
                                title={t("form.marketplace.categoryFilter.hover")}
                              />
                            </Button>
                            {categoryToggleTooltip.ToolTip}
                          </>
                      }
                    </>}
                    {MODULE_SDG_AVAILABLE && sdgs?.length > 0 && <>
                      {// SDG-button, depending on: sdgs chosen or not?
                        searchParams.sdgs?.length > 0
                          ? // are SDGs chosen?
                          <Button // Button for removing chosen filtering SDGs
                            className="project-filtering__btn"
                            id="sdg-filtering-toggler"
                            onClick={() => {
                              setSdgsExpanded(false)
                              setFieldValue("sdgs", [])
                              onSubmit({ ...values, sdgs: [] })
                            }}
                          >
                            {t("form.marketplace.sdgFilter.removeFilter")}
                            <Icon
                              name="trash"
                              size={25}
                              title={t("form.marketplace.sdgFilter.removeFilter")}
                            />
                          </Button>
                          : <>
                            <Button // Button for showing/hiding the SDGs
                              className="project-filtering__btn"
                              id="sdg-filtering-toggler"
                              onClick={toggleSdgsExpanded}
                              type="button"
                              disabled={!planningProjectsButtonChosen
                                && !runningProjectsButtonChosen
                                && !finishedProjectsButtonChosen
                              } // disable the button, if only ideas would be shown
                              innerRef={sdgToggleTooltip.tooltipTargetRef}
                            >
                              {t("form.marketplace.sdgFilter.label")}
                              <Icon
                                className={`${sdgsExpanded ? " project-filtering--active" : ""}`}
                                name="chevron-down"
                                size={25}
                                title={t("form.marketplace.sdgFilter.hover")}
                              />
                            </Button>
                            {sdgToggleTooltip.ToolTip}
                          </>
                      }
                    </>}
                  </div>
                </div>
              )}
            <div className="my-4 d-flex flex-column flex-sm-row justify-content-between align-items-center">
              <div className="text-left marketplace-stats">
                {// only show project statistics, if projects are shown (and not ideas)
                  (planningProjectsButtonChosen || runningProjectsButtonChosen || finishedProjectsButtonChosen)
                    ? <>
                      <MarketStatsDisplay
                        className="mr-3"
                        count={publicStatistics?.users || 0}
                        label={t("page.projects.index.statistics.cityCreators")}
                      />
                      <MarketStatsDisplay
                        count={publicStatistics?.activeProjects || 0}
                        label={t("page.projects.index.statistics.activeProjects")}
                      />
                    </>
                    : <MarketStatsDisplay
                      count={publicStatistics?.activeIdeas || 0}
                      label={t("page.projects.index.statistics.waitingIdeas")}
                    />
                }
                &emsp;
                <Link href={Routes.CreateIdea}>
                  <a className="btn primary-btn">
                    <Icon name="plus" size={25} />
                    {t("page.projects.index.addIdea")}
                  </a>
                </Link>
              </div>
              {values.sortBy &&
                <div className="flex-shrink-0 mt-2 mt-sm-0 align-self-end align-self-sm-auto">
                  <div className="d-flex align-items-center">
                    <Button
                      // @todo: warum ist das kein actionicon, obwohl es wie eines aussehen soll?
                      className="actionicon"
                      title={t(`form.marketplace.sort.label.${values.sortOrder}`)}
                      onClick={() => {
                        const newSortOrder =
                          values.sortOrder === IMarketSortOrder.DESC
                            ? IMarketSortOrder.ASC
                            : IMarketSortOrder.DESC

                        setFieldValue("sortOrder", newSortOrder)
                        onSubmit({ ...values, sortOrder: newSortOrder })
                      }}
                    >
                      <Icon
                        name={
                          values.sortOrder === IMarketSortOrder.DESC
                            ? "sort-descending"
                            : "sort-ascending"
                        }
                        size={25}
                      />
                    </Button>
                    <DropdownComponent
                      light
                      button={
                        <span
                          className="actionicon"
                          title={t("form.marketplace.sort.hover")}
                        >
                          <span>
                            {t(`form.marketplace.sort.${values.sortBy}`)}
                          </span>
                          <Icon
                            name={"caret"}
                            size={25}
                            title={t("form.marketplace.sort.hover")}
                          />
                        </span>
                      }
                    >
                      {sortingOptions.map((item) => {
                        return (
                          <a
                            key={item}
                            onClick={() => {
                              setFieldValue("sortBy", item)
                              onSubmit({ ...values, sortBy: item })
                            }}
                          >
                            {t(`form.marketplace.sort.${item}`)}
                          </a>
                        )
                      })}
                    </DropdownComponent>
                  </div>
                </div>
              }
            </div>
          </>
        </Form>
      }}
    </Formik>
  )
}

export default withDynamicNamespaces<IProps>(MarketSearchForm, usedNamespaces)